/**
 * <product-recommendations>
 *
 * Fills sections/section-related-products.liquid with products.
 *
 * The Liquid `recommendations` object is only ever populated when a section is
 * rendered THROUGH the Product Recommendations API together with the Section
 * Rendering API. On the product page's own render it is empty, so the section
 * arrives as an empty element and this component fetches the same section back
 * and swaps the populated markup in.
 *
 * Expected markup — the section renders this element as its outermost node, and
 * the response contains the same element with its content filled in:
 *
 *   <product-recommendations data-url data-section-id data-product-id …>
 *     …  ← empty on the page, populated in the response
 *   </product-recommendations>
 *
 * Two things about the swap are worth knowing:
 *
 * - `<script src>` tags injected via innerHTML never execute, which is why the
 *   section loads Swiper and product-carousel.js OUTSIDE this element. A
 *   `<style>` tag injected the same way does apply, so the section's scoped
 *   style block travels with the markup.
 * - `<product-carousel>` inside the injected markup is already a registered
 *   custom element, so it upgrades and runs its own connectedCallback the
 *   moment it lands in the DOM. There is nothing to initialise here.
 */

if (!customElements.get('product-recommendations')) {
  customElements.define(
    'product-recommendations',
    class ProductRecommendations extends HTMLElement {
      connectedCallback() {
        /* Already populated. This is the fetched render of the section being
           parsed by the DOMParser below — never a live page — but the guard is
           cheap and stops any chance of a fetch loop. */
        if (this.querySelector('.swiper-slide')) return;

        const productId = this.dataset.productId;
        const url = this.dataset.url;

        if (!productId || !url) return;

        /* Aborted in disconnectedCallback so a theme-editor section reload
           cannot land a stale response in an element that is no longer on the
           page. */
        this.controller = new AbortController();

        this.load(this.dataset.intent || 'related');
      }

      disconnectedCallback() {
        if (this.controller) {
          this.controller.abort();
          this.controller = null;
        }
      }

      /**
       * Fetch the section rendered with the given intent and swap it in.
       *
       * `complementary` returns nothing for any product the merchant has not
       * paired up in the Search & Discovery app. When that happens and the
       * merchant has asked for a fallback, retry once with the automatic
       * intent. Exactly one retry — `allowFallback` is what bounds it.
       */
      async load(intent, allowFallback = true) {
        const params = new URLSearchParams({
          section_id: this.dataset.sectionId,
          product_id: this.dataset.productId,
          limit: this.dataset.limit || '8',
          intent,
        });

        let markup;

        try {
          const response = await fetch(`${this.dataset.url}?${params}`, {
            signal: this.controller.signal,
          });

          if (!response.ok) return;

          markup = await response.text();
        } catch (error) {
          /* A failed cross-sell should be silent — the section simply does not
             appear. AbortError lands here too, and equally wants no output. */
          return;
        }

        const section = new DOMParser()
          .parseFromString(markup, 'text/html')
          .querySelector('product-recommendations');

        if (!section) return;

        const fallbackIntent = this.dataset.fallbackIntent;

        if (!section.querySelector('.swiper-slide')) {
          if (allowFallback && fallbackIntent && fallbackIntent !== intent) {
            return this.load(fallbackIntent, false);
          }
          return;
        }

        this.innerHTML = section.innerHTML;
      }
    },
  );
}
